import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays, Pill, Syringe, FlaskConical } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common";
import RecordsPage from "@/pages/RecordsPage";

const TABS = [
  { key: "appointments", label: "Appointments", icon: CalendarDays, perm: "page:appointments" },
  { key: "prescriptions", label: "Prescriptions", icon: Pill, perm: "page:prescriptions" },
  { key: "injectables", label: "Injectable PrEP", icon: Syringe, perm: "page:injectables" },
  { key: "labs", label: "Labs & Follow-Ups", icon: FlaskConical, perm: ["page:labs", "page:tasks"] },
];

export default function ClinicalManagement() {
  const { can } = useApp();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [counts, setCounts] = useState({});
  const [ck, setCk] = useState(0);
  const bump = useCallback(() => setCk((x) => x + 1), []);
  const allowed = TABS.filter((x) => [].concat(x.perm).some((p) => can(p)));
  const tab = allowed.find((x) => x.key === sp.get("tab"))?.key || allowed[0]?.key;
  const sub = sp.get("sub");
  useEffect(() => {
    api.get("/clinical/counts").then((r) => setCounts(r.data)).catch(() => {});
  }, [tab, ck]);
  if (!allowed.length) return <div className="panel p-10" data-testid="access-denied">You do not have access to this page.</div>;
  return (
    <div>
      <PageHeader title="Clinical Management" subtitle="Appointments, prescriptions, injectable PrEP, laboratory monitoring and follow-ups in one place." />
      <div className="mb-5 flex gap-1 overflow-x-auto rounded-xl border bg-[#EAF4FF] p-1 dark:bg-white/5" data-testid="clinical-tabs">
        {allowed.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => nav(`/clinical?tab=${key}`)} data-testid={`clinical-tab-${key}`}
            className={`flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === key ? "bg-white text-[#20336F] shadow-sm dark:bg-[var(--panel)] dark:text-blue-200" : "text-[var(--muted-ink)] hover:text-[#20336F]"}`}>
            <Icon size={15} />{label}
            {counts[key] != null && <span className={`rounded-full px-1.5 text-[11px] ${tab === key ? "bg-[#283F91] text-white" : "bg-white/70 text-[#283F91] dark:bg-white/10"}`} data-testid={`clinical-count-${key}`}>{counts[key]}</span>}
          </button>
        ))}
      </div>
      <div key={tab}>
        {tab === "appointments" && <RecordsPage kind="appointments" embedded initialSub={sub} onChanged={bump} />}
        {tab === "prescriptions" && <RecordsPage kind="prescriptions" embedded initialSub={sub} onChanged={bump} />}
        {tab === "injectables" && <RecordsPage kind="injections" embedded initialSub={sub} onChanged={bump} />}
        {tab === "labs" && (
          <>
            {can("page:labs") && <RecordsPage kind="labs" embedded initialSub={sub} onChanged={bump} />}
            {can("page:tasks") && (
              <div className="mt-8">
                <h2 className="mb-3 text-base font-semibold text-[#20336F] dark:text-blue-200 md:text-lg">Follow-up Tasks</h2>
                <RecordsPage kind="tasks" embedded onChanged={bump} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
